<template>
  <div v-if="show" class="dialog-overlay" @click="$emit('close')">
    <div class="dialog recharge-dialog" @click.stop>
      <div class="dialog-header">
        <h3>{{ $t('recharge.title') }}</h3>
        <div class="header-actions">
          <button class="records-btn" @click="goToRechargeRecords">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            {{ $t('recharge.viewRecords') }}
          </button>
          <button class="close-btn" @click="$emit('close')">×</button>
        </div>
      </div>
      
      <div class="dialog-content">
        <div class="recharge-tabs">
          <div class="tab-header">
            <button
              class="tab-button"
              :class="{ active: localRechargeType === 'easypay' }"
              @click="handleTabChange('easypay')"
            >
              {{ $t('recharge.easyPayRecharge') }}
            </button>
            <button
              class="tab-button"
              :class="{ active: localRechargeType === 'card' }"
              @click="handleTabChange('card')"
            >
              {{ $t('recharge.cardRecharge') }}
            </button>
            <button
              class="tab-button"
              :class="{ active: localRechargeType === 'usdt' }"
              @click="handleTabChange('usdt')"
            >
              {{ $t('recharge.usdtRecharge') }}
            </button>
          </div>
        </div>
        
        <div v-if="localRechargeType === 'card'" class="recharge-content">
          <div class="form-group">
            <label for="card-number">{{ $t('recharge.cardNumberLabel') }}</label>
            <input 
              id="card-number"
              type="text" 
              v-model="cardNumber" 
              :placeholder="$t('recharge.cardNumberPlaceholder')"
              class="form-input"
            />
          </div>
          <button 
            class="btn btn-primary recharge-btn" 
            @click="handleCardRecharge"
            :disabled="cardLoading"
          >
            <span v-if="cardLoading" class="btn-spinner"></span>
            {{ cardLoading ? $t('recharge.recharging') : $t('recharge.rechargeButton') }}
          </button>
          
          <!-- 卡密商城 -->
          <div class="card-shop-section" v-if="cardShopList.length > 0">
            <div class="divider">
              <span class="divider-text">{{ $t('recharge.cardShop') }}</span>
            </div>
            <div class="card-shop-buttons">
              <button 
                v-for="(shop, index) in cardShopList"
                :key="index"
                class="btn btn-outline card-shop-btn" 
                @click="openCardShop(shop.url)"
              >
                {{ shop.desc }}
              </button>
            </div>
          </div>
          
          <!-- 重要提示 -->
          <div class="recharge-tips">
            <div class="tips-header">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 9V13M12 17H12.01M12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ $t('recharge.tipsTitle') }}</span>
            </div>
            <ol class="tips-list">
              <li v-html="$t('recharge.tips.tip1')"></li>
              <li v-html="$t('recharge.tips.tip2')"></li>
              <li v-html="$t('recharge.tips.tip3')"></li>
            </ol>
          </div>
        </div>
        
        <div v-if="localRechargeType === 'usdt'" class="recharge-content">
          <div class="usdt-notice">
            <p class="usdt-notice-title"><b>{{ $t('mobile.recharge.usdt.notice') }}</b></p>
            <ol class="usdt-notice-list">
              <li>{{ $t('mobile.recharge.usdt.noticeItems.0') }}</li>
              <li>{{ $t('mobile.recharge.usdt.noticeItems.1') }}</li>
              <li>{{ $t('mobile.recharge.usdt.noticeItems.2') }}</li>
            </ol>
          </div>
          
          <div class="divider">
            <span class="divider-text">{{ $t('recharge.usdtProcessTitle') }}</span>
          </div>
          
          <div class="form-group mobile-form-group">
            <label for="usdt-amount-select">{{ $t('recharge.amount') }}</label>
            <select
              id="usdt-amount-select"
              v-model="usdtAmount"
              class="form-select"
              @change="handleUsdtAmountChange"
            >
              <option value="">{{ $t('recharge.amountPlaceholder') }}</option>
              <option value="50">{{ $t('recharge.amountOptions.50') }}</option>
              <option value="100">{{ $t('recharge.amountOptions.100') }}</option>
              <option value="200">{{ $t('recharge.amountOptions.200') }}</option>
              <option value="500">{{ $t('recharge.amountOptions.500') }}</option>
              <option value="1000">{{ $t('recharge.amountOptions.1000') }}</option>
              <option value="custom">{{ $t('recharge.customAmount') }}</option>
            </select>
          </div>

          <!-- 自定义金额输入框 -->
          <div v-if="usdtAmount === 'custom'" class="form-group mobile-form-group">
            <label for="custom-amount-input">{{ $t('recharge.customAmountLabel') }}</label>
            <input
              id="custom-amount-input"
              type="number"
              v-model.number="customAmount"
              :placeholder="$t('recharge.customAmountPlaceholder')"
              class="form-input"
              min="100"
              max="5000"
              @input="validateCustomAmount"
            />
            <div class="amount-hint">
              {{ $t('recharge.amountRange') }}: 100 - 5000
            </div>
            <div v-if="customAmountError" class="error-message">
              {{ customAmountError }}
            </div>
            <button
              class="btn btn-primary"
              style="margin-top: 12px;"
              @click="confirmCustomAmount"
              :disabled="isConfirmButtonDisabled || customAmountLoading"
            >
              <span v-if="customAmountLoading" class="loading-spinner"></span>
              <span v-else>{{ $t('recharge.confirmAmount') }}</span>
            </button>
          </div>

          <div class="divider" v-if="transactionInfo.price">
            <span class="divider-text">
              {{ $t('recharge.transactionInfoTitle') }} 
              <span class="price-text">{{ transactionInfo.price }}</span>
            </span>
          </div>
          
          <div class="transaction-info mobile-transaction-info" v-if="transactionInfo.address">
            <div class="transaction-row">
              <div class="transaction-col">
                <div class="copy-prompt">{{ $t('recharge.copyPrompt') }}</div>
                <div class="usdt-amount">
                  <span class="amount-value" @click="copyText(transactionInfo.usdtMoney)">
                    {{ transactionInfo.usdtMoney }}
                  </span>
                  <span class="amount-unit">USDT</span>
                </div>
                <div class="wallet-address" @click="copyText(transactionInfo.address)">
                  {{ transactionInfo.address }}
                </div>
              </div>
              <div class="qrcode-col">
                <div class="qrcode" v-if="transactionInfo.address">
                  <img 
                    :src="'https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=' + transactionInfo.address" 
                    :alt="$t('mobile.recharge.usdt.qrCodeAlt')" 
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div class="recharge-log" v-if="rechargeLog">
            <h4>{{ $t('recharge.rechargeLog') }}</h4>
            <pre>{{ rechargeLog }}</pre>
          </div>
        </div>

        <div v-if="localRechargeType === 'easypay'" class="recharge-content">
          <div class="easypay-section">
            <!-- 扫码充值帮助信息 -->
            <div class="easypay-help">
              <div class="help-header">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="2"/>
                  <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <span>{{ $t('recharge.easypay.helpTitle') }}</span>
              </div>
              <ol class="help-list">
                <li>{{ $t('recharge.easypay.help1') }}</li>
                <li>{{ $t('recharge.easypay.help2') }}</li>
                <li>{{ $t('recharge.easypay.help3') }}</li>
              </ol>
            </div>

            <div class="form-group">
              <label>{{ $t('recharge.amount') }}</label>
              <div class="amount-options">
                <button
                  v-for="amount in easyPayAmounts"
                  :key="amount"
                  class="amount-btn"
                  :class="{ active: easyPayAmount === amount }"
                  @click="easyPayAmount = amount"
                >
                  {{ amount }}金币
                </button>
              </div>
            </div>

            <div class="form-group">
              <label>{{ $t('recharge.paymentMethod') }}</label>
              <div class="payment-methods">
                <button
                  class="payment-btn"
                  :class="{ active: easyPayType === 'alipay' }"
                  @click="easyPayType = 'alipay'"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="#1677FF"/>
                    <path d="M8.5 12.5h7v1.5h-7v-1.5zm0-2h7v1h-7v-1z" fill="white"/>
                  </svg>
                  {{ $t('recharge.alipay') }}
                </button>
                <button
                  class="payment-btn"
                  :class="{ active: easyPayType === 'wxpay' }"
                  @click="easyPayType = 'wxpay'"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="#07C160"/>
                    <path d="M8.5 12.5h7v1.5h-7v-1.5zm0-2h7v1h-7v-1z" fill="white"/>
                  </svg>
                  {{ $t('recharge.wechatPay') }}
                </button>
              </div>
            </div>


            <button
              class="btn btn-primary recharge-btn"
              @click="handleEasyPayRecharge"
              :disabled="easyPayLoading || !finalAmount || !easyPayType"
            >
              <span v-if="easyPayLoading" class="btn-spinner"></span>
              {{ easyPayLoading ? $t('recharge.creating') : $t('recharge.createOrder', { amount: finalAmount }) }}
            </button>

            <!-- 支付信息展示 -->
            <div v-if="easyPayOrder" class="payment-info">
              <div class="divider">
                <span class="divider-text">{{ $t('recharge.paymentInfo') }}</span>
              </div>

              <div class="payment-card">
                <div class="payment-header">
                  <span class="order-no clickable" @click="copyText(easyPayOrder.outTradeNo)">
                    {{ $t('recharge.orderNo') }}: {{ easyPayOrder.outTradeNo }}
                  </span>
                  <span class="amount">{{ feeInfo.totalAmount }}元</span>
                </div>

                <!-- 费率明细 -->
                <div class="fee-details" v-if="Number(feeInfo.feeRate) > 0">
                  <div class="fee-detail-row">
                    <span>{{ $t('recharge.originalAmount') }}:</span>
                    <span>{{ feeInfo.originalAmount }}{{ $t('recharge.coins') }}</span>
                  </div>
                  <div class="fee-detail-row">
                    <span>{{ $t('recharge.feeAmount') }} ({{ feeInfo.feeRateDisplay }}):</span>
                    <span>+{{ feeInfo.feeAmount }}元</span>
                  </div>
                  <div class="fee-detail-row fee-detail-total">
                    <span>{{ $t('recharge.totalPayment') }}:</span>
                    <span>{{ feeInfo.totalAmount }}元</span>
                  </div>
                </div>

                <div class="payment-content">
                  <div class="payment-center" v-if="easyPayOrder.url">
                    <div class="qr-section">
                      <p class="qr-tip">{{ $t('recharge.scanToPayTip') }}</p>
                      <div class="qr-code">
                        <canvas
                          ref="qrCanvas"
                          :width="qrCodeSize"
                          :height="qrCodeSize"
                          :alt="$t('recharge.paymentQrAlt')"
                        ></canvas>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="payment-status">
                  <div class="status-item">
                    <span class="status-dot" :class="paymentStatusClass"></span>
                    <span>{{ paymentStatusText }}</span>
                  </div>
                  <button
                    v-if="!paymentSuccess"
                    class="btn btn-outline btn-sm"
                    @click="checkPaymentStatus"
                    :disabled="checkingStatus"
                  >
                    {{ checkingStatus ? $t('recharge.checking') : $t('recharge.checkStatus') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { getRechargeList, createEasyPayOrder, checkEasyPayResult } from '@/api/sms'
import QRCode from 'qrcode-generator'

export default {
  name: 'RechargeDialog',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    rechargeType: {
      type: String,
      default: 'easypay'
    },
    transactionInfo: {
      type: Object,
      default: () => ({})
    },
    rechargeLog: {
      type: String,
      default: ''
    },
    cardLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localRechargeType: this.rechargeType,
      cardNumber: '',
      usdtAmount: '',
      customAmount: null,
      customAmountError: '',
      customAmountLoading: false,
      cardShopList: [],
      // 易支付相关
      easyPayAmounts: [5, 10, 20, 30, 50, 80, 100],
      easyPayAmount: null,
      easyPayType: 'alipay',
      easyPayLoading: false,
      easyPayOrder: null,
      paymentSuccess: false,
      checkingStatus: false,
      statusCheckTimer: null,
      qrCodeSize: 120,
      // 费率相关
      feeInfo: {
        originalAmount: 0,
        feeAmount: 0,
        totalAmount: 0,
        feeRate: 0,
        feeRateDisplay: ''
      }
    }
  },
  watch: {
    rechargeType(val) {
      this.localRechargeType = val;
    },
    show(val) {
      if (val) {
        // Fetch card shop list when dialog opens
        this.fetchCardShopList();
      } else {
        // Reset form when dialog closes
        this.cardNumber = '';
        this.usdtAmount = '';
        this.customAmount = null;
        this.customAmountError = '';
        this.customAmountLoading = false;
        this.resetEasyPayData();
      }
    },
    easyPayOrder: {
      handler(newOrder) {
        if (newOrder && newOrder.outTradeNo) {
          this.$nextTick(() => {
            let qrUrl;
            // 如果是非首充用户,直接使用支付链接URL
            if (newOrder.isFirstRecharge === false && newOrder.url) {
              qrUrl = newOrder.url;
            } else {
              // 首充用户使用中间校验页URL
              qrUrl = `${window.location.origin}/pay-verify?outTradeNo=${newOrder.outTradeNo}`;
            }
            this.generateQRCode(qrUrl);
          });
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    if (this.show) {
      this.fetchCardShopList();
    }
  },
  computed: {
    finalAmount() {
      return this.easyPayAmount;
    },
    paymentStatusClass() {
      if (this.paymentSuccess) return 'success';
      return 'pending';
    },
    paymentStatusText() {
      if (this.paymentSuccess) return this.$t('recharge.paymentSuccess');
      return this.$t('recharge.paymentPending');
    },
    isConfirmButtonDisabled() {
      // Button is disabled if:
      // 1. customAmount is null, undefined, 0, or empty string
      // 2. There's a validation error

      // Convert to number if it's a string
      const amount = typeof this.customAmount === 'string'
        ? parseFloat(this.customAmount)
        : this.customAmount;

      // Check if we have a valid positive number
      const hasValidAmount = amount &&
                            !isNaN(amount) &&
                            amount > 0;

      return !hasValidAmount || !!this.customAmountError;
    }
  },
  methods: {
    // 获取卡密商城列表
    async fetchCardShopList() {
      try {
        const response = await getRechargeList();
        if (response.success && response.data && response.data.rechargeList) {
          this.cardShopList = response.data.rechargeList;
        }
      } catch (error) {
        console.error('获取卡密商城失败:', error);
      }
    },
    // 打开卡密商城
    openCardShop(url) {
      if (url) {
        window.open(url, '_blank');
      }
    },
    handleTypeChange() {
      this.$emit('type-change', this.localRechargeType);
    },
    handleTabChange(type) {
      this.localRechargeType = type;
      this.$emit('type-change', type);
    },
    handleCardRecharge() {
      if (!this.cardNumber.trim()) {
        ElMessage.warning(this.$t('recharge.cardNumberRequired'));
        return;
      }
      this.$emit('card-recharge', this.cardNumber);
    },
    handleUsdtAmountChange() {
      // Reset custom amount and error when switching to preset amounts
      if (this.usdtAmount !== 'custom') {
        this.customAmount = null
        this.customAmountError = ''
        // Emit the selected amount value
        if (this.usdtAmount) {
          this.$emit('usdt-amount-change', this.usdtAmount)
        }
      } else {
        // When custom is selected, clear the transaction info
        this.customAmountError = ''
      }
    },
    validateCustomAmount() {
      // Clear previous error first
      this.customAmountError = ''

      // Convert to number if it's a string
      const amount = typeof this.customAmount === 'string'
        ? parseFloat(this.customAmount)
        : this.customAmount

      // If empty or not a valid number, don't show error (just disable button)
      if (!amount || isNaN(amount)) {
        return
      }

      // Validate range
      if (amount < 100) {
        this.customAmountError = this.$t('recharge.amountTooLow')
        return
      }

      if (amount > 5000) {
        this.customAmountError = this.$t('recharge.amountTooHigh')
        return
      }
    },
    confirmCustomAmount() {
      // Prevent duplicate clicks
      if (this.customAmountLoading) {
        return
      }

      // Validate before confirming
      this.validateCustomAmount()

      // Convert to number for validation
      const amount = typeof this.customAmount === 'string'
        ? parseFloat(this.customAmount)
        : this.customAmount

      // If there's an error or invalid amount, don't proceed
      if (this.customAmountError || !amount || isNaN(amount)) {
        return
      }

      // Set loading state
      this.customAmountLoading = true

      // Emit the custom amount as a string
      this.$emit('usdt-amount-change', amount.toString())
    },
    // 重置自定义金额loading状态(由父组件调用)
    resetCustomAmountLoading() {
      this.customAmountLoading = false
    },
    async copyText(text) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        ElMessage.success(this.$t('messages.copySuccess'));
      } catch (err) {
        ElMessage.error(this.$t('messages.copyError'));
      }
    },
    // 易支付相关方法
    async handleEasyPayRecharge() {
      if (!this.finalAmount) {
        ElMessage.warning(this.$t('recharge.pleaseSelectAmount'));
        return;
      }
      if (!this.easyPayType) {
        ElMessage.warning(this.$t('recharge.pleaseSelectPaymentMethod'));
        return;
      }
      if (this.finalAmount < 5 || this.finalAmount > 300) {
        ElMessage.warning(this.$t('recharge.amountRangeError'));
        return;
      }

      this.easyPayLoading = true;
      try {
        const orderData = {
          type: this.easyPayType,
          money: this.finalAmount,
          name: '余额充值',
          device: 'jump'
        };

        const response = await createEasyPayOrder(orderData);

        if (response.status === 200) {
          this.easyPayOrder = response.data;

          // 更新费率信息
          this.feeInfo = {
            originalAmount: response.data.originalAmount || this.finalAmount,
            feeAmount: response.data.feeAmount || 0,
            totalAmount: response.data.totalAmount || this.finalAmount,
            feeRate: response.data.feeRate || 0,
            feeRateDisplay: response.data.feeRateDisplay || '0%'
          };

          ElMessage.success(this.$t('recharge.orderCreatedSuccess'));

          // 开始轮询支付状态
          this.startStatusPolling();
        } else {
          throw new Error(response.msg || this.$t('recharge.createOrderFailed'));
        }
      } catch (error) {
        console.error('创建订单失败:', error);
        ElMessage.error(error.message || this.$t('recharge.createOrderFailed'));
      } finally {
        this.easyPayLoading = false;
      }
    },
    async checkPaymentStatus() {
      if (!this.easyPayOrder || this.paymentSuccess) return;

      this.checkingStatus = true;
      try {
        const response = await checkEasyPayResult(this.easyPayOrder.tradeNo);

        if (response.status === 200 && response.data) {
          if (response.data.success) {
            this.paymentSuccess = true;
            this.stopStatusPolling();
            ElMessage.success(this.$t('recharge.paymentSuccessMessage'));

            // 通知父组件充值成功
            this.$emit('payment-success', {
              type: 'easypay',
              amount: this.finalAmount,
              order: this.easyPayOrder
            });
          }
        }
      } catch (error) {
        console.error('查询支付状态失败:', error);
        ElMessage.error(this.$t('recharge.checkStatusFailed'));
      } finally {
        this.checkingStatus = false;
      }
    },
    startStatusPolling() {
      // 清除之前的计时器
      if (this.statusCheckTimer) {
        clearInterval(this.statusCheckTimer);
      }

      // 每10秒检查一次支付状态
      this.statusCheckTimer = setInterval(() => {
        if (!this.paymentSuccess && this.easyPayOrder) {
          this.checkPaymentStatus();
        } else {
          this.stopStatusPolling();
        }
      }, 10000);
    },
    stopStatusPolling() {
      if (this.statusCheckTimer) {
        clearInterval(this.statusCheckTimer);
        this.statusCheckTimer = null;
      }
    },
    resetEasyPayData() {
      this.easyPayAmount = null;
      this.easyPayType = 'alipay';
      this.easyPayOrder = null;
      this.paymentSuccess = false;
      this.checkingStatus = false;
      this.feeInfo = {
        originalAmount: 0,
        feeAmount: 0,
        totalAmount: 0,
        feeRate: 0,
        feeRateDisplay: ''
      };
      this.stopStatusPolling();
    },
    generateQRCode(url) {
      try {
        const qr = QRCode(0, 'M');
        qr.addData(url);
        qr.make();

        const canvas = this.$refs.qrCanvas;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const moduleCount = qr.getModuleCount();
        const cellSize = this.qrCodeSize / moduleCount;

        // 清空画布
        ctx.clearRect(0, 0, this.qrCodeSize, this.qrCodeSize);

        // 绘制白色背景
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, this.qrCodeSize, this.qrCodeSize);

        // 绘制二维码
        ctx.fillStyle = '#000000';
        for (let row = 0; row < moduleCount; row++) {
          for (let col = 0; col < moduleCount; col++) {
            if (qr.isDark(row, col)) {
              ctx.fillRect(col * cellSize, row * cellSize, cellSize, cellSize);
            }
          }
        }
      } catch (error) {
        console.error('生成二维码失败:', error);
      }
    },
    goToRechargeRecords() {
      this.$emit('close');
      this.$router.push('/user-center');
    }
  }
}
</script>

<style scoped>
/* 充值对话框 */
.recharge-dialog {
  max-width: 600px;
  width: 90%;
}

/* 对话框头部样式 */
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.records-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  color: #6B7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.records-btn:hover {
  border-color: #4461F2;
  color: #4461F2;
  background: rgba(68, 97, 242, 0.05);
}

.records-btn svg {
  flex-shrink: 0;
}

/* Tab样式 */
.recharge-tabs {
  margin-bottom: 24px;
}

.tab-header {
  display: flex;
  border-bottom: 2px solid #E5E7EB;
  position: relative;
}

.tab-button {
  flex: 1;
  padding: 14px 20px;
  background: transparent;
  border: none;
  font-size: 15px;
  font-weight: 600;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.tab-button:hover {
  color: #4461F2;
  background: rgba(68, 97, 242, 0.05);
}

.tab-button.active {
  color: #4461F2;
  background: transparent;
}

.tab-button.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #4461F2 0%, #38BFFF 100%);
}

.recharge-content {
  padding: 24px 0 16px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* USDT充值样式 */
.usdt-notice {
  background: #FEF3C7;
  border: 1px solid #FCD34D;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.usdt-notice-title {
  font-size: 14px;
  color: #92400E;
  margin-bottom: 12px;
}

.usdt-notice-list {
  margin: 0;
  padding-left: 20px;
}

.usdt-notice-list li {
  font-size: 13px;
  color: #78350F;
  line-height: 1.6;
  margin-bottom: 8px;
}

.usdt-notice-list li::marker {
  color: #FB923C;
  font-weight: 600;
}

.divider {
  display: flex;
  align-items: center;
  margin: 24px 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background-color: var(--border-color);
}

.divider-text {
  padding: 0 16px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
}

.price-text {
  color: #F44336;
  font-weight: 600;
}

/* USDT交易信息样式 */
.transaction-info {
  background: linear-gradient(135deg, #F0F4FF 0%, #E8ECFF 100%);
  border: 1px solid #C7D2FE;
  border-radius: 20px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(68, 97, 242, 0.08);
}

.transaction-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.transaction-col {
  flex: 1;
}

.copy-prompt {
  font-size: 12px;
  color: var(--text-tertiary);
  margin-bottom: 8px;
}

.usdt-amount {
  margin-bottom: 16px;
}

.amount-value {
  font-size: 24px;
  font-weight: 700;
  color: #F44336;
  cursor: pointer;
}

.amount-value:hover {
  text-decoration: underline;
}

.amount-unit {
  font-size: 14px;
  color: var(--text-secondary);
  margin-left: 4px;
}

.wallet-address {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: var(--text-secondary);
  word-break: break-all;
  padding: 8px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  cursor: pointer;
}

.wallet-address:hover {
  background: rgba(255, 255, 255, 0.8);
}

.qrcode-col {
  width: 100px;
  flex-shrink: 0;
}

.qrcode img {
  width: 100px;
  height: 100px;
  border-radius: var(--radius-sm);
}

/* 充值日志 */
.recharge-log {
  margin-top: 20px;
  padding: 16px;
  background: #F3F4F6;
  border-radius: 8px;
}

.recharge-log h4 {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.recharge-log pre {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #4B5563;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
}

.recharge-btn {
  width: 100%;
}


/* 费率明细样式 */
.fee-details {
  margin: 16px 0;
  padding: 12px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
}

.fee-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 13px;
  color: #6B7280;
}

.fee-detail-row:last-child {
  margin-bottom: 0;
}

.fee-detail-row.fee-detail-total {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.8);
  font-weight: 600;
  color: #374151;
}

/* 移动端响应式 */
@media (max-width: 768px) {
  .recharge-dialog {
    max-width: 100%;
    width: 100%;
    height: 100%;
    border-radius: 0;
    margin: 0;
  }
  
  .tab-button {
    padding: 12px 16px;
    font-size: 14px;
  }
  
  .transaction-info .transaction-row,
  .mobile-transaction-info .transaction-row {
    flex-direction: column;
    gap: 20px;
  }
  
  .transaction-info .transaction-col,
  .mobile-transaction-info .transaction-col {
    width: 100%;
    text-align: center;
  }
  
  .transaction-info .qrcode-col,
  .mobile-transaction-info .qrcode-col {
    width: 100%;
    display: flex;
    justify-content: center;
  }
  
  .transaction-info .qrcode img,
  .mobile-transaction-info .qrcode img {
    width: 120px;
    height: 120px;
  }
}

/* 重要提示样式 */
.recharge-tips {
  margin-top: 20px;
  padding: 16px;
  background: rgba(251, 191, 36, 0.05);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 8px;
}

.tips-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #D97706;
}

.tips-header svg {
  color: #F59E0B;
}

.tips-list {
  margin: 0;
  padding-left: 20px;
  color: #6B7280;
  line-height: 1.8;
}

.tips-list li {
  margin-bottom: 8px;
  font-size: 14px;
}

.tips-list li:last-child {
  margin-bottom: 0;
}

.tips-list strong {
  color: #374151;
  font-weight: 600;
}

.tips-list strong.warning {
  color: #DC2626;
  font-weight: 700;
  background: rgba(239, 68, 68, 0.1);
  padding: 2px 4px;
  border-radius: 4px;
}

/* 移动端适配 */
@media (max-width: 640px) {
  .recharge-tips {
    margin-top: 16px;
    padding: 12px;
  }
  
  .tips-header {
    font-size: 15px;
    margin-bottom: 10px;
  }
  
  .tips-list {
    padding-left: 18px;
  }
  
  .tips-list li {
    font-size: 13px;
    margin-bottom: 6px;
  }
  
  /* 卡密商城样式 */
  .card-shop-section {
    margin-top: 20px;
  }
  
  .card-shop-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 15px;
  }
  
  .card-shop-btn {
    padding: 10px 20px;
    border: 1px solid #e0e0e0;
    background: white;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s;
    font-size: 14px;
    color: #333;
  }
  
  .card-shop-btn:hover {
    border-color: #007aff;
    color: #007aff;
    background: rgba(0, 122, 255, 0.05);
  }
  
  .btn-outline {
    background: transparent;
  }
}

/* 易支付样式 */
.easypay-section {
  padding: 0;
}

/* 扫码充值帮助样式 */
.easypay-help {
  background: linear-gradient(135deg, #E0F2FE 0%, #E0E7FF 100%);
  border: 1px solid #BAE6FD;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}

.help-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #0369A1;
}

.help-header svg {
  color: #0284C7;
  flex-shrink: 0;
}

.help-list {
  margin: 0;
  padding-left: 20px;
  color: #334155;
  line-height: 1.8;
}

.help-list li {
  margin-bottom: 8px;
  font-size: 13px;
}

.help-list li:last-child {
  margin-bottom: 0;
  color: #DC2626;
  font-weight: 500;
}

.amount-options {
  display: flex;
  gap: 10px;
  margin: 10px 0;
  flex-wrap: wrap;
}

.amount-btn {
  padding: 12px 20px;
  border: 2px solid #E5E7EB;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.amount-btn:hover {
  border-color: #4461F2;
  color: #4461F2;
}

.amount-btn.active {
  border-color: #4461F2;
  background: #4461F2;
  color: white;
}

.payment-methods {
  display: flex;
  gap: 12px;
  margin: 10px 0;
  flex-wrap: wrap;
}

.payment-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: 2px solid #E5E7EB;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  min-width: 120px;
  justify-content: center;
}

.payment-btn:hover {
  border-color: #4461F2;
  color: #4461F2;
}

.payment-btn.active {
  border-color: #4461F2;
  background: #4461F2;
  color: white;
}

.payment-btn svg {
  flex-shrink: 0;
}

.payment-info {
  margin-top: 24px;
}

.payment-card {
  background: linear-gradient(135deg, #F0F4FF 0%, #E8ECFF 100%);
  border: 1px solid #C7D2FE;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(68, 97, 242, 0.08);
}

.payment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
}

.payment-content {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px 0;
}

.payment-center {
  width: 100%;
  text-align: center;
}

.order-no {
  font-size: 12px;
  color: #6B7280;
  font-family: 'Courier New', monospace;
}

.order-no.clickable {
  cursor: pointer;
  transition: color 0.3s;
}

.order-no.clickable:hover {
  color: #4461F2;
  text-decoration: underline;
}

.amount {
  font-size: 18px;
  font-weight: 700;
  color: #F44336;
}

.qr-section {
  text-align: center;
}

.qr-tip {
  font-size: 12px;
  color: #6B7280;
  margin: 0 0 10px;
}

.qr-code {
  background: white;
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: inline-block;
}

.qr-code canvas {
  display: block;
  border-radius: 4px;
}


.payment-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #F59E0B;
}

.status-dot.success {
  background: #10B981;
}

.btn-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 移动端易支付适配 */
@media (max-width: 768px) {
  .amount-options {
    gap: 8px;
  }

  .amount-btn {
    flex: 1;
    min-width: 0;
    padding: 10px 12px;
    font-size: 13px;
  }

  .payment-methods {
    flex-direction: column;
  }

  .payment-btn {
    width: 100%;
    min-width: 0;
  }

  .payment-content {
    margin: 16px 0;
  }

  .payment-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .payment-status {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .status-item {
    justify-content: center;
  }
}

/* 自定义金额输入样式 */
.amount-hint {
  font-size: 12px;
  color: #6B7280;
  margin-top: 4px;
}

.error-message {
  font-size: 13px;
  color: #DC2626;
  margin-top: 4px;
  font-weight: 500;
}

/* Loading spinner样式 */
.loading-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spinner-rotate 0.6s linear infinite;
}

@keyframes spinner-rotate {
  to {
    transform: rotate(360deg);
  }
}
</style>
